/**
 * 前端统一错误对象。
 *
 * 后端的**正常**失败响应体是 `{ ok: false, error: { code, message, details } }`
 * （见方案 4.6），其中的 message 已经是给用户看的中文文案，可以直接展示，
 * 不要在前端再拼一遍文案，否则会出现"两套说法"。
 *
 * ⚠️ 但"正常"不等于"全部"：请求体没过 zod 时，Fastify 会用它**默认**的错误体
 *    `{ statusCode, error, message }`，而 `message` 是 `ZodError.message` ——
 *    一串 JSON（2026-10-18 管理员空密码登录就是这么把 JSON 送上屏幕的）。
 *    所以"给用户看哪句话"统一由 `./failure.ts` 决定，本文件只做类型与出口。
 */
import type { ErrorCode } from '@fht/shared';
import { DEFAULT_FAILURE_MESSAGE, describeFailureMessage } from './failure';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: ErrorCode, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** 判断错误码，避免调用处到处写字符串比较。 */
  is(code: ErrorCode): boolean {
    return this.code === code;
  }
}

/** 网络层失败（断网、代理未起、后端没启动）。 */
export class NetworkError extends ApiError {
  constructor(message = '无法连接服务器，请检查网络后重试') {
    super('INTERNAL', message, 0);
    this.name = 'NetworkError';
  }
}

/**
 * 会话彻底失效：refresh 也救不回来。
 * 为了让 React Query 的 onError 不用 instanceof 判断，这里同样继承 ApiError。
 */
export class SessionExpiredError extends ApiError {
  constructor() {
    super('SESSION_REVOKED', '登录状态已失效，请重新登录', 401);
    this.name = 'SessionExpiredError';
  }
}

/**
 * 把任意错误转成可展示的中文文案。
 *
 * ⚠️ 这里**曾经**直接 `return error.message`。于是当后端把 zod 的 issues 序列化成
 *    字符串塞进 `message`（Fastify 默认错误体，见 `./failure.ts` 顶部的链路说明）时，
 *    页面会把一整串 JSON 直接渲染出来 —— 2026-10-18 用户实测：管理员登录页
 *    不填密码点「登录」，屏幕上就是一串 JSON。
 *
 *    现在统一走 `describeFailureMessage()`：正常文案原样返回；JSON 只取里面的人话；
 *    取不到人话就用 `fallback`。**任何调用点都不会再拿到 `{"…"}`**。
 */
export function toErrorMessage(error: unknown, fallback = DEFAULT_FAILURE_MESSAGE): string {
  return describeFailureMessage(error, fallback);
}

/** 后端未实现该接口时（404/501/502/503），用于优雅降级而不是报错。 */
export function isNotImplemented(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    !(error instanceof NetworkError) &&
    (error.status === 404 || error.status === 501 || error.status === 502 || error.status === 503)
  );
}
